import { Card, CardContent, CardHeader } from './card';
import { Badge } from './badge';
import { Button } from './button';
import { Progress } from './progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from './dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { ReactNode } from 'react';
import { RiskAssessmentScores } from '../RiskAssessmentScores';
import { CardActionMenuItem, ListCardAction, getClickableCardProps } from './list-card';

export type GridCardAction = ListCardAction;

export interface GridCardBadge {
  label: string;
  variant?: 'default' | 'secondary' | 'outline';
  className?: string;
}

export interface GridCardMetric {
  label: string;
  value: string | number;
  icon?: ReactNode;
  className?: string;
}

export interface GridCardProgress {
  label: string;
  value: number;
  className?: string;
}

export interface GridCardProps {
  title: string;
  description?: string;
  badges?: GridCardBadge[];
  metrics?: GridCardMetric[];
  progress?: GridCardProgress[];
  vulnerabilities?: {
    critical?: number;
    high?: number;
    medium?: number;
    low?: number;
  };
  riskScores?: {
    overallScore?: number;
    vulnerabilityScore?: number;
    licenseScore?: number;
    operationalRisk?: number;
    technicalDebt?: number;
    licenseIssues?: number;
  };
  actions?: GridCardAction[];
  /** Makes the whole card clickable -- see getClickableCardProps. */
  onClick?: () => void;
  /** Page `onClick` opens; used for Ctrl/Cmd+click and middle-click to open it in a new tab. */
  href?: string;
  children?: ReactNode;
  className?: string;
}

export function GridCard({
  title,
  description,
  badges = [],
  metrics = [],
  progress = [],
  vulnerabilities,
  riskScores,
  actions = [],
  onClick,
  href,
  children,
  className = ""
}: GridCardProps) {
  const { className: clickableClassName = '', ...clickableProps } = getClickableCardProps(title, onClick, href);

  return (
    <Card
      className={`rounded-lg shadow-sm border border-border hover:shadow-md transition-shadow ${clickableClassName} ${className}`}
      {...clickableProps}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2 flex-wrap">
              <h3 className="font-semibold text-foreground text-lg">{title}</h3>
              {badges.map((badge, index) => (
                <Badge key={index} className={badge.className} variant={badge.variant}>
                  {badge.label}
                </Badge>
              ))}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground mb-3">{description}</p>
            )}
            {metrics.length > 0 && (
              <div className="flex items-center space-x-4 flex-wrap text-sm text-muted-foreground">
                {metrics.map((metric, index) => (
                  <span key={index} className={`flex items-center ${metric.className || ''}`}>
                    {metric.icon}
                    <span className='font-medium'>{metric.label}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {actions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actions.map((action, index) => (
                  <CardActionMenuItem key={index} action={action} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
       
        {riskScores && (
          <div className="mb-4">
            <RiskAssessmentScores riskScores={riskScores} compact={false} />
          </div>
        )}

        {children}
      </CardContent>
    </Card>
  );
}
